import { useQueryClient } from "@tanstack/react-query";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";

import Button from "@/components/Button";
import { FormLayout, Input, Label } from "@/components/Forms";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import VideoField, { Dropzone } from "@/components/Forms/Fields/VideoField";
import { api } from "@/utils/api";
import { VideoFramesExportType } from "@/utils/enums";
import { handleFormErrors } from "@/utils/helpers";
import { hexToRgba } from "@/utils/theme";
import useAlerts from "@/hooks/useAlerts";
import DurationSlider from "./DurationSlider";
import ExportTypeField from "./ExportTypeField";
import FpsField from "./FpsField";
import DurationField from "./DurationField";

const Box = styled.div`
  margin-bottom: 24px;
  padding: 24px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 0px 0px 4px 4px;
  ${Dropzone} {
    padding: 38px;
  }
  label {
    font-size: 11px;
    line-height: 15px;
  }
`;

const Separator = styled.div`
  height: 1px;
  margin: 24px 0;
  background: ${({ theme }) => theme.colors.lightGray};
`;

const ButtonsHolder = styled.div`
  margin: 32px 0 48px;
  display: flex;
  justify-content: right;
`;

const SecondaryButton = styled(Button)`
  border-color: ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.black};
  margin-right: 16px;
  &:hover {
    border-color: ${({ theme }) => theme.colors.black};
    background-color: transparent;
    color: ${({ theme }) => theme.colors.black};
  }
`;

const SubmitButton = styled(Button)`
  &:disabled {
    border-color: ${({ theme }) => theme.colors.grayscaleLight};
    color: ${({ theme }) => theme.colors.gray};
    background: ${({ theme }) => hexToRgba(theme.colors.black, 0.05)};
  }
`;

const DurationFields = styled.div`
  display: grid;
  justify-content: space-between;
  grid-template-columns: repeat(2, 280px);
`;

export interface FormDataT {
  video?: string;
  time_from: number;
  time_to: number;
  fps: number;
  name: string;
  type: VideoFramesExportType;
}

const ExtractorForm = () => {
  const [videoDuration, setVideoDuration] = useState(0);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const methods = useForm<FormDataT>({
    defaultValues: {
      name: "",
      fps: 1,
      time_from: 0,
      time_to: 0,
      type: VideoFramesExportType.FULL_VIDEO,
    },
  });

  const {
    register,
    control,
    handleSubmit,
    clearErrors,
    setError,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  useEffect(() => {
    setValue("time_to", videoDuration);
    setValue(
      "type",
      videoDuration
        ? VideoFramesExportType.PART_VIDEO
        : VideoFramesExportType.FULL_VIDEO
    );
  }, [videoDuration, setValue]);

  const type = watch("type");

  const videoUploadedCallback = useCallback((file: File) => {
    const videoEl = document.createElement("video");
    videoEl.preload = "metadata";

    videoEl.onloadedmetadata = () => {
      window.URL.revokeObjectURL(videoEl.src);
      setVideoDuration(
        isNaN(videoEl.duration) ? 0 : Math.floor(videoEl.duration)
      );
    };

    videoEl.src = URL.createObjectURL(file);
  }, []);

  const onClear = () => {
    setVideoDuration(0);
    reset();
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.post<FormDataT, { id: number }>(
          "/v1/video-frames-exports",
          data
        );

        queryClient.invalidateQueries(["video_frames_exports"]);

        alerts.success("Extract request has been successfully created");

        onClear();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts);
      }
    })(e);
  };

  return (
    <form onSubmit={onSubmit}>
      <Box>
        <VideoField<FormDataT>
          name="video"
          control={control}
          videoUploadedCallback={videoUploadedCallback}
          notes={
            <>
              Format: MP4, MOV
              <br />
              Max file size: 5GB
            </>
          }
          videoTypes={["video/mp4", "video/quicktime"]}
        />
      </Box>
      <Box>
        <FormLayout>
          <div>
            <Label>Extract name</Label>
            <Input
              {...register("name")}
              placeholder="Add name"
              $hasError={!!errors.name}
              autoComplete="off"
              $small
            />
            {!!errors.name && (
              <ValidationMessage>{errors.name.message}</ValidationMessage>
            )}
          </div>
          <FpsField<FormDataT> control={control} name="fps" />
        </FormLayout>
        {!!videoDuration && (
          <>
            <Separator />
            <ExportTypeField control={control} name="type" />
            {type === VideoFramesExportType.PART_VIDEO && (
              <>
                <DurationSlider control={control} maxValue={videoDuration} />
                <DurationFields>
                  <DurationField
                    control={control}
                    name="time_from"
                    label="Time from"
                  />
                  <DurationField
                    control={control}
                    name="time_to"
                    label="Time to"
                  />
                </DurationFields>
              </>
            )}
          </>
        )}
      </Box>
      <ButtonsHolder>
        <SecondaryButton type="button" onClick={onClear}>
          Clear
        </SecondaryButton>
        <SubmitButton
          type="submit"
          loading={isSubmitting}
          disabled={isSubmitting || !!!videoDuration}
        >
          Extract
        </SubmitButton>
      </ButtonsHolder>
    </form>
  );
};

export default ExtractorForm;
